// ─────────────────────────────────────────────────────────────────────────────
// standalone_host.ts — In-process host bridge for PWA mode
//
// Replaces the VS Code extension host (grimoire_panel.ts).  Instead of
// communicating over postMessage, the webview calls methods on this object
// directly.  Persistence goes to IndexedDB instead of the filesystem.
//
// The CanvasApp receives a HostBridge interface; in VS Code mode it wraps
// postMessage, in PWA mode it wraps this class.
// ─────────────────────────────────────────────────────────────────────────────

import * as persistence from './vault_dispatch';
import {
  loadHandle as loadVaultHandle,
  saveHandle as saveVaultHandle,
  clearHandle as clearVaultHandle,
  ensurePermission as ensureVaultPermission,
  isFsaSupported,
} from './vault_handle_store';
import type { GrimoireAction } from '@shared/grimoire_action';
import {
  journalClient as httpJournalClient,
  type IJournalClient,
  type JournalInfo,
  type TreeNode,
  type FolderInfo,
} from './journal_client';
import { FsaJournalClient } from './fsa_journal_client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type StrokeData = persistence.StrokeData;

/** Synthetic page id prefix for folder landing pages. The path that follows
 *  is the folder's forward-slash relative id (empty string for root). */
export const FOLDER_LANDING_PREFIX = '_folder:';

/** Build the synthetic landing-page id for a folder path. */
export function landingPageId(folderPath: string): string {
  return FOLDER_LANDING_PREFIX + folderPath;
}

interface PageEntry {
  id: string;
  index: number;
  title: string;
  color?: string;
  sectionId?: string;
  isSectionHeader?: boolean;
  densityLevel: number;
  /** Per-page decoration slots: body sub-slots + margins sub-slots
   *  + frame. Empty buckets dropped at save time. */
  decorations?: {
    body?: { justification?: string; graphics?: string };
    margins?: { utility?: string; graphics?: string };
    frame?: string;
  };
  bookmark?: unknown;
  blocks: Map<string, Record<string, unknown>>;
  arrows: Map<string, Record<string, unknown>>;
  updatedAt: number;
  /** Journal-mode only: relative file path inside the journal (e.g. "Page 1.ox.json").
   *  Absent in IDB mode. Tracked separately from `id` so canvas-facing page ids
   *  stay stable even when filenames differ (new pages are born with a
   *  timestamp id, but their file is named after their title). */
  journalPath?: string;
  /** 'folder-landing' for synthetic landing pages (one per folder, including root);
   *  undefined / 'page' for normal user pages. */
  kind?: 'page' | 'folder-landing';
  /** Folder this landing page represents — only set when kind === 'folder-landing'. */
  folderPath?: string;
  /** Two-line description rendered under the title on landing pages. */
  description?: string;
  /** Set when the most recent _ensurePageLoaded attempt for this page
   *  threw or returned no usable data. Surfaces in the side rail as a
   *  red-cross icon and lets the canvas render an explicit error
   *  state instead of a silently empty page. Cleared on a successful
   *  re-load. */
  loadFailed?: boolean;
}

interface SectionEntry {
  id: string;
  title: string;
  description: string;
  color: string;
  pageIds: string[];
  headerPageId: string;
  kind?: string;
  createdAt: number;
}

/** The interface CanvasApp uses to talk to its host. */
export interface HostBridge {
  postMessage(msg: unknown): void;
  getState(): unknown;
  setState(state: unknown): void;
}

// ── Standalone Host ───────────────────────────────────────────────────────────

export class StandaloneHost {
  private _pages: PageEntry[] = [];
  private _sections: SectionEntry[] = [];
  private _currentPageIndex = 0;
  private _currentPageId: string | undefined;
  private _pageStrokes = new Map<string, Map<string, StrokeData>>();
  private _strokesDirty = false;
  private _indexDirty = false;
  private _strokesSinceLastSave = 0;
  private _autoSaveTimer: ReturnType<typeof setInterval> | undefined;
  private _meta = {
    id: 'standalone-' + Date.now().toString(36),
    title: 'The OX Journal',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    coverSeed: Math.floor(Math.random() * 10000),
  };

  // ── Journal mode (set by init() if /api/journal is reachable, or if a
  //    vault folder handle activates the FSA-backed client) ─────────────────
  private _journalMode = false;
  private _journalInfo: JournalInfo | null = null;
  /** Active IJournalClient — HTTP-backed when run_pwa.py is reachable,
   *  FSA-backed when the user has picked a vault folder, null when neither
   *  is available (legacy IDB-only mode). */
  private _client: IJournalClient | null = null;
  /** Folder tree, lazily expanded as the user navigates. At boot we fetch
   *  depth=1 (root + immediate children); subfolders get attached on first
   *  visit via _ensureFolderLoaded. */
  private _journalTree: TreeNode | null = null;
  /** Folder paths whose direct children have already been merged into
   *  _journalTree and _pages. Root ("") is added at the end of init. */
  private _loadedFolders = new Set<string>();
  /** In-flight folder-load promises so concurrent navigations dedupe. */
  private _folderLoading = new Map<string, Promise<void>>();
  /** Real page ids whose blocks/arrows/strokes have been hydrated from disk.
   *  Stub PageEntry objects exist before this point with empty Maps. */
  private _loadedPages = new Set<string>();
  /** In-flight page-content fetches so concurrent navigations dedupe. */
  private _pageLoading = new Map<string, Promise<void>>();
  /** Per-folder landing-page metadata (title override + 2-line description). */
  private _folderInfo = new Map<string, FolderInfo>();
  /** Per-page dirty tracker — queue of page ids that need saving to disk. */
  private _journalDirty = new Set<string>();
  /** Per-page debounce timers — coalesce rapid actions into one save. */
  private _journalSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Pages currently being written (to avoid overlapping POSTs for one page). */
  private _journalSavesInFlight = new Set<string>();
  private static readonly _JOURNAL_SAVE_DEBOUNCE_MS = 600;

  /** Callback to send messages to the webview (set by pwa_main). */
  private _sendToWebview: ((msg: unknown) => void) | null = null;

  /** Buffer for messages sent before the webview sink is wired. */
  private _pendingMessages: unknown[] = [];

  private _state: Record<string, unknown> = {};

  /** The display name for this journal — from the journal manifest in journal
   *  mode, otherwise the default "The OX Journal". Callers use this for
   *  window.title surfacing after init. */
  notebookTitle(): string {
    return this._meta.title || 'The OX Journal';
  }

  /**
   * Journal-mode only. Returns a flat content list for the *given folder*
   * (defaults to the root) — its immediate children only. This is what
   * folder landing pages render: a focused per-folder index, not the whole
   * vault. Each row carries a `pageId` for navigation: subfolders point to
   * the subfolder's landing page, files point to themselves.
   *
   * Returns null when not in journal mode, tree not loaded, or the folder
   * hasn't been explored yet (the caller should _ensureFolderLoaded first).
   */
  getJournalContents(folderPath: string = ''): Array<{
    id: string;
    title: string;
    level: number;
    isFolder: boolean;
    pageId?: string;
  }> | null {
    if (!this._journalMode || !this._journalTree) return null;
    const node = folderPath === '' ? this._journalTree : this._findTreeNode(folderPath);
    if (!node) return null;

    const pathToId = new Map<string, string>();
    for (const page of this._pages) {
      if (page.journalPath) pathToId.set(page.journalPath, page.id);
    }

    return (node.children ?? []).map(child => {
      const isFolder = child.isContainer;
      return {
        id: child.id,
        title: child.title || (child.id.split('/').pop()?.replace(/\.ox\.json$/i, '') ?? ''),
        level: 0,
        isFolder,
        pageId: isFolder ? landingPageId(child.id) : pathToId.get(child.id),
      };
    });
  }

  /** Folder landing-page metadata for the canvas to render the title /
   *  description. Returns null when the folder isn't loaded yet. */
  getFolderLanding(folderPath: string): {
    folderPath: string;
    title: string;
    description: string;
  } | null {
    if (!this._journalMode) return null;
    const meta = this._folderInfo.get(folderPath);
    if (!meta) return null;
    return {
      folderPath,
      title: meta.title,
      description: meta.description,
    };
  }

  /** Async kick-off for folder hydration triggered from the canvas (e.g.
   *  when a tab click navigates into a so-far-unloaded folder). The promise
   *  rejects only on transport errors; idempotent otherwise. */
  loadFolder(folderPath: string): Promise<void> {
    return this._ensureFolderLoaded(folderPath);
  }

  /** Async kick-off for page hydration. Idempotent. */
  loadPage(pageId: string): Promise<void> {
    return this._ensurePageLoaded(pageId);
  }

  /** True iff the page id is the synthetic id for a folder landing page. */
  isFolderLanding(pageId: string): boolean {
    return pageId.startsWith(FOLDER_LANDING_PREFIX);
  }

  /**
   * Journal-mode only.  Returns the siblings of the given canvas page id —
   * all nodes (files and folders) that share the same parent directory.
   * Also returns parent + grandparent info so the caller can build
   * "level up" / back navigation.
   * Returns null when not in journal mode, tree not loaded, or page unknown.
   */
  getJournalSiblings(pageId: string): {
    /** Forward-slash path of the parent folder (relative to journal root). */
    parentPath: string;
    parentTitle: string;
    /** grandparent path for the "level up again" action; null at root. */
    grandparentPath: string | null;
    items: Array<{
      /** Tree node id: journalPath for files, directory path for folders. */
      id: string;
      title: string;
      isFolder: boolean;
      /** Canvas page id — the folder's landing, or the file's own id. */
      pageId?: string;
      /** True for the tab representing the currently-displayed page. */
      isActive: boolean;
    }>;
  } | null {
    if (!this._journalMode || !this._journalTree) return null;

    // Folder landings sit "in" their folder for sibling purposes — fall
    // through to the folder-anchored variant so the side rail behaves the
    // same whether the user is on a leaf page or its folder's landing.
    if (this.isFolderLanding(pageId)) {
      const folderPath = pageId.slice(FOLDER_LANDING_PREFIX.length);
      const ctx = this.getJournalSiblingsAtFolder(folderPath);
      if (!ctx) return null;
      return {
        ...ctx,
        items: ctx.items.map(item => ({ ...item, isActive: false })),
      };
    }

    // Build reverse maps: canvas-id ↔ journalPath
    const idToPath = new Map<string, string>();
    const pathToId = new Map<string, string>();
    for (const page of this._pages) {
      if (page.journalPath) {
        idToPath.set(page.id, page.journalPath);
        pathToId.set(page.journalPath, page.id);
      }
    }

    const journalPath = idToPath.get(pageId);
    if (!journalPath) return null;

    // Walk the tree to locate the parent of our target node.
    const findParent = (
      node: TreeNode,
      targetId: string,
      parent: TreeNode | null,
      grandparent: TreeNode | null,
    ): { parent: TreeNode; grandparent: TreeNode | null } | null => {
      for (const child of node.children ?? []) {
        if (child.id === targetId) return { parent: node, grandparent };
        const found = findParent(child, targetId, node, parent);
        if (found) return found;
      }
      return null;
    };

    const found = findParent(this._journalTree, journalPath, null, null);
    if (!found) return null;
    const { parent, grandparent } = found;

    const items = (parent.children ?? []).map(child => {
      const isFolder = child.isContainer;
      // Folders navigate to their landing page, not their first leaf — the
      // landing IS where the user wants to be when they click a folder tab.
      const childPageId = isFolder ? landingPageId(child.id) : pathToId.get(child.id);
      return {
        id: child.id,
        title: child.title || (child.id.split('/').pop()?.replace(/\.ox\.json$/i, '') ?? ''),
        isFolder,
        pageId: childPageId,
        isActive: child.id === journalPath,
      };
    });

    return {
      parentPath: parent.id,
      parentTitle: parent.title || (parent.id.split('/').pop() ?? ''),
      grandparentPath: grandparent?.id ?? null,
      items,
    };
  }

  /**
   * Return the folder-level navigation context for `folderPath` itself —
   * i.e. the view where `folderPath`'s children are the sidebar items.
   * Used by "level up" when there is no history page at the grandparent level:
   * we update the nav context in-place rather than navigating to a new page.
   */
  getJournalSiblingsAtFolder(folderPath: string): {
    parentPath: string;
    parentTitle: string;
    grandparentPath: string | null;
    items: Array<{
      id: string;
      title: string;
      isFolder: boolean;
      pageId?: string;
      isActive: boolean;
    }>;
  } | null {
    if (!this._journalMode || !this._journalTree) return null;

    const pathToId = new Map<string, string>();
    for (const page of this._pages) {
      if (page.journalPath) pathToId.set(page.journalPath, page.id);
    }

    // Walk tree to find the target node and its parent.
    const findWithParent = (
      node: TreeNode,
      targetId: string,
      parent: TreeNode | null,
    ): { node: TreeNode; parent: TreeNode | null } | null => {
      if (node.id === targetId) return { node, parent };
      for (const child of node.children ?? []) {
        const r = findWithParent(child, targetId, node);
        if (r) return r;
      }
      return null;
    };

    // The root node has id '' — handle that specially.
    const found = folderPath === ''
      ? { node: this._journalTree, parent: null }
      : findWithParent(this._journalTree, folderPath, null);
    if (!found) return null;
    const { node, parent } = found;

    const items = (node.children ?? []).map(child => ({
      id: child.id,
      title: child.title || (child.id.split('/').pop()?.replace(/\.ox\.json$/i, '') ?? ''),
      isFolder: child.isContainer,
      // Folders navigate to their landing page (where the user sees the
      // folder title + description + content index), not their first leaf.
      pageId: child.isContainer ? landingPageId(child.id) : pathToId.get(child.id),
      isActive: false,
    }));

    return {
      parentPath: node.id,
      parentTitle: node.title || (node.id.split('/').pop() ?? ''),
      grandparentPath: parent?.id ?? null,
      items,
    };
  }

  /** Wire the webview message sink and flush any buffered messages. */
  setSendToWebview(fn: (msg: unknown) => void): void {
    this._sendToWebview = fn;
    // Flush any messages that arrived before the sink was wired
    const pending = this._pendingMessages;
    this._pendingMessages = [];
    for (const msg of pending) {
      fn(msg);
    }
  }

  /** Create the HostBridge interface that CanvasApp expects. */
  createBridge(): HostBridge {
    return {
      postMessage: (msg: unknown) => this._onWebviewMessage(msg as { type: string; payload?: unknown }),
      getState: () => this._state,
      setState: (s: unknown) => { this._state = (s ?? {}) as Record<string, unknown>; },
    };
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  /** Load the notebook. Preference order:
   *    1. If /api/journal is reachable (run_pwa.py), drive the journal via HTTP.
   *    2. Else, if a vault folder handle is saved AND has live `readwrite`
   *       permission, drive the journal via the File System Access API. The
   *       same `_initFromJournal` lifecycle runs against either client —
   *       lazy tree, landing pages, session restore, all of it.
   *    3. Otherwise fall back to IndexedDB (pure-PWA / no folder picked yet).
   *  Within a single run we don't switch modes — the choice made here holds. */
  async init(): Promise<void> {
    // Try to silently restore a previously chosen vault folder. This sets
    // the FSA backend in `vault_dispatch` AND gives us a directory handle
    // we can hand to FsaJournalClient. No prompt — that requires a user
    // gesture and is wired through `vault.activate` / `vault.detach`.
    const restoredHandle = await this._restoreVaultHandle();

    // 1. HTTP first — if run_pwa.py is up, it owns the folder.
    const httpInfo = await httpJournalClient.probe();
    if (httpInfo) {
      this._client = httpJournalClient;
      await this._initFromJournal(httpInfo);
    }
    // 2. FSA — user-picked folder.
    else if (restoredHandle) {
      const fsaClient = new FsaJournalClient(restoredHandle);
      const fsaInfo = await fsaClient.probe();
      if (fsaInfo) {
        this._client = fsaClient;
        await this._initFromJournal(fsaInfo);
      } else {
        // Manifest creation failed — fall through.
        await this._initFromIdb();
      }
    }
    // 3. No journal source → ephemeral IDB-only mode.
    else {
      await this._initFromIdb();
    }

    // Start auto-save timer. In journal mode we flush any pending page dirty
    // queue (debounced timers should normally catch these first; this is
    // the safety net). In IDB mode we flush the legacy index+strokes bundle.
    this._autoSaveTimer = setInterval(() => {
      if (this._journalMode) {
        this._flushJournalDirty();
      } else if (this._strokesDirty || this._indexDirty) {
        this._flushState();
      }
    }, 10_000);
  }

  /** Lazy boot. Only fetch:
   *    1. Root folder tree at depth=1 (just the root's immediate children).
   *    2. Root folder landing-page metadata (title + 2-line description).
   *    3. The folder containing the previous session's currentPageId, if any.
   *
   *  No page contents are read at init — every PageEntry starts as a stub
   *  (empty blocks/arrows). _ensurePageLoaded fetches contents on first
   *  navigation. _ensureFolderLoaded merges deeper subtrees on demand when
   *  the user clicks a folder tab or navigates "up".
   *
   *  This replaces the old eager loop that read every page at boot — for
   *  large vaults that loop dominated startup time (one HTTP round-trip +
   *  JSON parse + base64 stroke decode per page). */
  private async _initFromJournal(info: JournalInfo): Promise<void> {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this._journalMode = true;
    this._journalInfo = info;
    this._meta = {
      ...this._meta,
      id: 'journal-' + (info.root || '').replace(/\W+/g, '-').toLowerCase().slice(-40),
      title: info.title || 'The OX Journal',
      createdAt: info.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    // Previous session state (last page, last folder, ...) travels with the
    // journal so a re-open puts the user back where they were.
    const session = await this._client!.loadSession();
    const sessionCurrent = (session as Record<string, unknown>).currentPageId;
    const sessionFolder = (session as Record<string, unknown>).currentFolderPath;

    // Root tree at depth=1 — root + immediate children only. Subfolders are
    // empty containers in this initial tree; their children land lazily.
    const tree = await this._client!.tree({ depth: 1 });
    this._journalTree = tree;
    this._loadedFolders.add('');

    // Root landing-page metadata. A failure here (server doesn't support
    // /api/folder/info yet, fresh journal, etc.) just falls through to
    // defaults — the landing still renders, just with an empty description.
    try {
      const rootMeta = await this._client!.getFolderInfo('');
      this._folderInfo.set('', rootMeta);
    } catch {
      this._folderInfo.set('', { path: '', title: tree.title, description: '' });
    }

    // Materialise root's landing + its direct page leaves as stubs. Folder
    // children at this depth show up as containers in the tree but get no
    // entry in _pages until visited.
    this._appendFolderEntries('');

    // Empty journal → make one blank page so the canvas has something real.
    if (this._pages.filter(p => !this._isLandingEntry(p)).length === 0) {
      const pageId = 'page-' + Date.now().toString(36);
      const journalPath = 'Page 1.ox.json';
      const entry: PageEntry = {
        id: pageId,
        journalPath,
        index: this._pages.length,
        title: 'Page 1',
        densityLevel: 0,
        blocks: new Map(),
        arrows: new Map(),
        updatedAt: Date.now(),
        kind: 'page',
      };
      this._pages.push(entry);
      this._loadedPages.add(pageId);
      // Splice into the tree so sibling/landing queries see it immediately.
      this._spliceTreeLeaf(journalPath, 'Page 1');
      await this._savePageImmediate(pageId, { create: true });
    }

    // If the previous session's folder isn't root, also fetch that folder so
    // the user lands in the same place without an extra round-trip after the
    // canvas paints.
    if (typeof sessionFolder === 'string' && sessionFolder && sessionFolder !== '') {
      try {
        await this._ensureFolderLoaded(sessionFolder);
      } catch (err) {
        console.warn('[journal] failed to restore session folder', sessionFolder, err);
      }
    }

    // Pick current page: prefer session, fall back to root landing.
    let currentIdx = -1;
    if (typeof sessionCurrent === 'string') {
      currentIdx = this._pages.findIndex(p => p.id === sessionCurrent);
    }
    if (currentIdx === -1) {
      const rootLandingId = landingPageId('');
      currentIdx = this._pages.findIndex(p => p.id === rootLandingId);
    }
    if (currentIdx === -1) currentIdx = 0;
    this._currentPageIndex = currentIdx;
    this._currentPageId = this._pages[currentIdx]?.id;

    const elapsed = ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0) | 0;
    console.log(`[journal] lazy init: ${elapsed} ms, ${this._pages.length} entries (no page contents loaded)`);
  }

  // ── Lazy folder/page hydration ──────────────────────────────────────────

  private _isLandingEntry(p: PageEntry): boolean {
    return p.kind === 'folder-landing';
  }

  /** Build a stub PageEntry from a leaf TreeNode — title + journalPath only,
   *  empty blocks/arrows. Hydration happens lazily via _ensurePageLoaded. */
  private _stubFromTreeNode(leaf: TreeNode, index: number): PageEntry {
    const idFromPath = leaf.id.replace(/\.ox\.json$/i, '');
    return {
      id: idFromPath,
      journalPath: leaf.id,
      index,
      title: leaf.title || idFromPath,
      densityLevel: 0,
      blocks: new Map(),
      arrows: new Map(),
      updatedAt: 0,
      kind: 'page',
    };
  }

  /** Build a synthetic landing PageEntry for a folder. */
  private _makeLandingEntry(folderPath: string, fallbackTitle: string, description: string): PageEntry {
    return {
      id: landingPageId(folderPath),
      journalPath: undefined,
      index: 0,
      title: this._folderInfo.get(folderPath)?.title || fallbackTitle,
      densityLevel: 0,
      blocks: new Map(),
      arrows: new Map(),
      updatedAt: Date.now(),
      kind: 'folder-landing',
      folderPath,
      description: this._folderInfo.get(folderPath)?.description || description,
    };
  }

  /** Append a folder's landing + its direct page leaves to _pages. The folder
   *  must already be in _journalTree (loaded by _ensureFolderLoaded or root
   *  init). Mutates _pages in place. */
  private _appendFolderEntries(folderPath: string): PageEntry[] {
    const folderNode = folderPath === '' ? this._journalTree : this._findTreeNode(folderPath);
    if (!folderNode) return [];
    const fallbackTitle = folderPath === ''
      ? (this._meta.title || 'Journal')
      : (folderNode.title || folderPath.split('/').pop() || folderPath);

    const added: PageEntry[] = [];
    // Landing first — it's the conceptual "first page" of the folder.
    const landing = this._makeLandingEntry(folderPath, fallbackTitle, '');
    if (!this._pages.some(p => p.id === landing.id)) {
      landing.index = this._pages.length;
      this._pages.push(landing);
      added.push(landing);
    }
    // Then every leaf child as a stub.
    for (const child of folderNode.children ?? []) {
      if (child.isContainer) continue;  // subfolder landings come on demand
      if (this._pages.some(p => p.journalPath === child.id)) continue;
      const stub = this._stubFromTreeNode(child, this._pages.length);
      this._pages.push(stub);
      added.push(stub);
    }
    return added;
  }

  /** Walk _journalTree to find the node with the given id (forward-slash
   *  relative path). Returns null if not found / tree not yet loaded that deep. */
  private _findTreeNode(id: string): TreeNode | null {
    if (!this._journalTree) return null;
    if (id === '') return this._journalTree;
    const stack: TreeNode[] = [this._journalTree];
    while (stack.length) {
      const n = stack.pop()!;
      if (n.id === id) return n;
      for (const child of n.children ?? []) stack.push(child);
    }
    return null;
  }

  /** Fetch a folder's depth=1 subtree + landing metadata, splice it into the
   *  in-memory tree, append its landing + leaves to _pages, and notify the
   *  webview so its NotebookState picks up the new pages. Idempotent. */
  private async _ensureFolderLoaded(folderPath: string): Promise<void> {
    if (!this._journalMode) return;
    if (this._loadedFolders.has(folderPath)) return;
    const inflight = this._folderLoading.get(folderPath);
    if (inflight) return inflight;

    const promise = (async () => {
      let subtree: TreeNode;
      try {
        subtree = await this._client!.tree({ path: folderPath, depth: 1 });
      } catch (err) {
        console.warn('[journal] folder tree fetch failed:', folderPath, err);
        return;
      }
      // Splice subtree.children into the in-tree node.
      const node = this._findTreeNode(folderPath);
      if (node) {
        node.children = subtree.children ?? [];
      }
      // Folder landing-page metadata — best-effort, defaults if missing.
      try {
        const meta = await this._client!.getFolderInfo(folderPath);
        this._folderInfo.set(folderPath, meta);
      } catch {
        this._folderInfo.set(folderPath, {
          path: folderPath,
          title: subtree.title || folderPath.split('/').pop() || folderPath,
          description: '',
        });
      }
      const added = this._appendFolderEntries(folderPath);
      this._loadedFolders.add(folderPath);
      // Tell the webview about the new pages so its NotebookState can route
      // navigation to them. Each emitted action is shaped like a normal
      // page.create so canvas_app's existing handler picks them up.
      for (const entry of added) {
        this._emitSyntheticPageCreate(entry);
      }
    })();

    this._folderLoading.set(folderPath, promise);
    try {
      await promise;
    } finally {
      this._folderLoading.delete(folderPath);
    }
  }

  /** Push a page.create action to the webview to advertise an entry that
   *  appeared via lazy folder expansion. Canvas treats this as a brand-new
   *  page; its blocks/arrows are still empty until _ensurePageLoaded fires. */
  private _emitSyntheticPageCreate(entry: PageEntry): void {
    const action: GrimoireAction = {
      type: 'page.create',
      pageId: entry.id,
      insertAfterIndex: Math.max(0, entry.index - 1),
      title: entry.title,
      color: entry.color,
      origin: 'system',
      timestamp: Date.now(),
    } as GrimoireAction;
    // Tag so the canvas can treat folder-landing entries specially.
    const payload = action as unknown as Record<string, unknown>;
    if (entry.kind === 'folder-landing') {
      payload.kind = 'folder-landing';
      payload.folderPath = entry.folderPath;
      payload.description = entry.description ?? '';
    }
    this._send({ type: 'action', payload: action });
  }

  /** Fetch the page's actual contents (blocks + arrows + strokes) on demand
   *  and notify the webview. Idempotent. Folder landings short-circuit. */
  private async _ensurePageLoaded(pageId: string): Promise<void> {
    if (!this._journalMode) return;
    const entry = this._pages.find(p => p.id === pageId);
    if (!entry) return;
    if (entry.kind === 'folder-landing') return;
    if (this._loadedPages.has(pageId)) return;
    const inflight = this._pageLoading.get(pageId);
    if (inflight) return inflight;

    const promise = (async () => {
      if (!entry.journalPath) return;
      try {
        const data = await this._client!.readPage(entry.journalPath);
        this._hydrateEntry(entry, data);
        this._loadedPages.add(pageId);
        // Successful load — clear any prior fault flag.
        if (entry.loadFailed) {
          entry.loadFailed = false;
          this._send({ type: 'page.loadFailed', pageId, failed: false });
        }
      } catch (err) {
        console.error('[journal] page read failed for', entry.journalPath, err);
        entry.loadFailed = true;
        // Tell the canvas so it can flag the side-tab and render an
        // explicit error state instead of a silently empty page.
        this._send({ type: 'page.loadFailed', pageId, failed: true });
      }
    })();

    this._pageLoading.set(pageId, promise);
    try {
      await promise;
    } finally {
      this._pageLoading.delete(pageId);
    }
  }

  /** Populate an existing PageEntry from a journal page-read response.
   *  Mirrors what _entryFromJournalRead used to do at init, except we now
   *  mutate the existing stub in place (preserving its index) and notify
   *  the webview so its NotebookState absorbs the freshly-loaded blocks. */
  private _hydrateEntry(entry: PageEntry, data: { meta?: unknown; blocks?: unknown[]; arrows?: unknown[]; strokes?: unknown[] }): void {
    const meta = (data.meta ?? {}) as Record<string, unknown>;
    if (typeof meta.title === 'string' && meta.title) entry.title = meta.title;
    if (typeof meta.color === 'string') entry.color = meta.color;
    if (typeof meta.densityLevel === 'number') entry.densityLevel = meta.densityLevel;
    if (typeof meta.updatedAt === 'number') entry.updatedAt = meta.updatedAt;

    entry.blocks.clear();
    for (const block of (data.blocks ?? []) as Array<Record<string, unknown>>) {
      if (typeof block.id === 'string') entry.blocks.set(block.id, { ...block });
    }
    entry.arrows.clear();
    const arrowsFromTop = (data as unknown as { arrows?: Array<Record<string, unknown>> }).arrows ?? [];
    for (const ar of arrowsFromTop) {
      if (typeof ar.id === 'string') entry.arrows.set(ar.id, { ...ar });
    }
    const strokeMap = new Map<string, StrokeData>();
    for (const stroke of (data.strokes ?? []) as StrokeData[]) {
      if (stroke && typeof stroke.id === 'string') {
        strokeMap.set(stroke.id, {
          id: stroke.id,
          points: (stroke.points ?? []).map(p => ({ ...p })),
          style: stroke.style ?? {},
          timestamp: stroke.timestamp ?? 0,
          pageLayer: stroke.pageLayer,
        });
      }
    }
    this._pageStrokes.set(entry.id, strokeMap);
  }

  /** IndexedDB boot path — the original behaviour, preserved for when no
   *  journal server is reachable. */
  private async _initFromIdb(): Promise<void> {
    const existing = await persistence.loadIndex();
    if (existing) {
      this._meta = existing.meta;
      this._sections = (existing.sections ?? []) as SectionEntry[];
      this._pages = existing.pages.map(p => {
        // Pages from the FSA-native loader come with blocks/arrows inlined
        // (because the page state lives inside each .ox.json file rather
        // than being reconstructed from an action log). IDB-mode pages
        // leave both arrays undefined and rely on _replayActions below.
        const blocks = new Map<string, Record<string, unknown>>();
        const arrows = new Map<string, Record<string, unknown>>();
        if (Array.isArray(p.blocks)) {
          for (const b of p.blocks) {
            if (b && typeof b === 'object' && (b as { id?: string }).id) {
              blocks.set((b as { id: string }).id, b as Record<string, unknown>);
            }
          }
        }
        if (Array.isArray(p.arrows)) {
          for (const a of p.arrows) {
            if (a && typeof a === 'object' && (a as { id?: string }).id) {
              arrows.set((a as { id: string }).id, a as Record<string, unknown>);
            }
          }
        }
        return {
          id: p.id,
          index: p.index,
          title: p.title,
          color: p.color,
          sectionId: p.sectionId,
          isSectionHeader: p.isSectionHeader,
          densityLevel: p.densityLevel,
          bookmark: p.bookmark,
          decorations: p.decorations,
          blocks,
          arrows,
          updatedAt: p.updatedAt ?? Date.now(),
        };
      });

      // Find current page
      if (existing.currentPageId) {
        this._currentPageId = existing.currentPageId;
        const idx = this._pages.findIndex(p => p.id === existing.currentPageId);
        if (idx !== -1) this._currentPageIndex = idx;
      }

      // Replay actions to rebuild block/arrow/stroke state. In FSA mode
      // loadAllActions returns [] (page state lives inline in page files,
      // so there's nothing to replay) — this becomes a cheap no-op.
      await this._replayActions();

      // FSA-mode supplement: actions don't carry stroke data in this mode,
      // so _replayActions left _pageStrokes empty. Pull strokes eagerly
      // from each page file. The vault adapter's cache is already warm
      // from loadIndex, so this loop is O(n) cache hits, not directory
      // walks.
      if (persistence.isFsaActive()) {
        for (const page of this._pages) {
          const strokes = await persistence.loadPageStrokes(page.id);
          if (strokes.length > 0) {
            const map = new Map<string, persistence.StrokeData>();
            for (const s of strokes) {
              if (s && typeof s === 'object' && (s as { id?: string }).id) {
                map.set((s as { id: string }).id, s);
              }
            }
            this._pageStrokes.set(page.id, map);
          }
        }
      }
    } else {
      // Fresh notebook — create one blank page
      const pageId = 'page-' + Date.now().toString(36);
      this._pages = [{
        id: pageId,
        index: 0,
        title: 'Page 1',
        densityLevel: 0,
        blocks: new Map(),
        arrows: new Map(),
        updatedAt: Date.now(),
      }];
      this._currentPageIndex = 0;
      await this._saveIndex();
    }
  }

  // ── Vault folder (FSA) management ─────────────────────────────────────────

  /** On boot: try to silently restore a saved directory handle. Activates the
   *  FSA persistence backend if the handle exists AND its readwrite permission
   *  is still 'granted' from a previous session. If permission fell back to
   *  'prompt' (typical after browser restart), we keep the handle stored but
   *  default to IDB until the user re-picks the folder via the settings UI. */
  private async _restoreVaultHandle(): Promise<FileSystemDirectoryHandle | null> {
    if (!isFsaSupported()) return null;
    try {
      const handle = await loadVaultHandle();
      if (!handle) return null;
      const perm = await ensureVaultPermission(handle, 'readwrite', false);
      if (perm === 'granted') {
        persistence.setActiveHandle(handle);
        return handle;
      }
    } catch (e) {
      console.warn('[vault] restore failed:', e);
    }
    return null;
  }

  /** Activate FSA mode against a directory handle obtained by the canvas. */
  private async _activateVault(handle: FileSystemDirectoryHandle | undefined): Promise<void> {
    if (!handle) return;
    try {
      await saveVaultHandle(handle);
      persistence.setActiveHandle(handle);
      this._sendVaultStatus();
      this._send({ type: 'vault.reloadRequired' });
    } catch (e) {
      console.error('[vault] activate failed:', e);
    }
  }

  /** Detach the active FSA vault and revert to IndexedDB-only persistence. */
  private async _detachVault(): Promise<void> {
    try {
      await clearVaultHandle();
      persistence.setActiveHandle(null);
      this._sendVaultStatus();
      this._send({ type: 'vault.reloadRequired' });
    } catch (e) {
      console.error('[vault] detach failed:', e);
    }
  }

  /** Push current vault status to the canvas so the settings UI can render. */
  private _sendVaultStatus(): void {
    const handle = persistence.getActiveHandle();
    this._send({
      type: 'vault.status',
      payload: {
        supported: isFsaSupported(),
        active: handle !== null,
        folderName: handle?.name ?? null,
      },
    });
  }

  dispose(): void {
    if (this._autoSaveTimer) {
      clearInterval(this._autoSaveTimer);
      this._autoSaveTimer = undefined;
    }
    if (this._journalMode) {
      // Fire any outstanding debounced saves immediately so we don't lose work
      // on tab close. The returned promise isn't awaited — dispose() is sync —
      // but the fetch() calls will still complete against a running server.
      for (const timer of this._journalSaveTimers.values()) clearTimeout(timer);
      this._journalSaveTimers.clear();
      for (const pageId of this._journalDirty) {
        void this._savePageImmediate(pageId);
      }
      this._journalDirty.clear();
    } else if (this._strokesDirty || this._indexDirty) {
      this._flushState();
    }
  }

  // ── Message handling (mirrors grimoire_panel.ts _onWebviewMessage) ────────

  private _onWebviewMessage(msg: { type: string; payload?: unknown; [k: string]: unknown }): void {
    switch (msg.type) {
      case 'ready':
        this._handleReady();
        break;

      case 'action':
        this._handleAction(msg.payload as GrimoireAction, !!(msg as Record<string, unknown>).locallyApplied);
        break;

      case 'page.requestStrokes':
        void this._navigateAndLoad((msg as unknown as { pageId: string }).pageId);
        break;

      case 'page.navigate': {
        const navPageId = (msg as unknown as { pageId: string }).pageId;
        if (this._journalMode) {
          this._flushJournalDirty();
        } else if (this._strokesDirty || this._indexDirty) {
          this._flushState();
        }
        this._currentPageId = navPageId;
        const idx = this._pages.findIndex(p => p.id === navPageId);
        if (idx !== -1) this._currentPageIndex = idx;
        if (this._journalMode) this._persistJournalSession();
        // Hydrate on demand so the next render isn't blocked on a fetch.
        if (this._journalMode) void this._ensurePageLoaded(navPageId);
        break;
      }

      case 'settings.save':
        persistence.saveSettings(msg.payload as Record<string, unknown>).catch(console.error);
        break;

      case 'settings.load':
        persistence.loadSettings().then(settings => {
          this._send({ type: 'settings.data', payload: settings });
        }).catch(() => {
          this._send({ type: 'settings.data', payload: null });
        });
        break;

      case 'vault.activate':
        void this._activateVault(
          (msg.payload as { handle?: FileSystemDirectoryHandle } | undefined)?.handle,
        );
        break;

      case 'vault.detach':
        void this._detachVault();
        break;

      case 'vault.requestStatus':
        this._sendVaultStatus();
        break;

      case 'wizard.query': {
        // In PWA mode without a host-side LLM, return a graceful placeholder.
        // The real agent path in canvas_app._invokeAgent bypasses this entirely.
        const wq = msg as { content?: string; context?: Record<string, unknown> };
        const pageId = (wq.context as Record<string, unknown>)?.pageId as string ?? '';
        this._send({
          type: 'action',
          payload: {
            type: 'wizard.respond',
            marginalia: {
              pageId,
              text: 'The wizard is resting. Try invoking an arcane channel to summon a character.',
              glyphStyle: 'calm',
              x: 40, y: 100,
            },
            origin: 'agent',
            timestamp: Date.now(),
          },
        });
        break;
      }

      case 'strokes.transform':
        this._handleStrokeTransform(msg as unknown as {
          pageId: string; strokeIds: string[];
          transform: { kind: string; dx?: number; dy?: number; px?: number; py?: number; sx?: number; sy?: number; angle?: number };
        });
        break;

      default:
        console.warn('[StandaloneHost] Unknown message type:', msg.type);
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  private _handleReady(): void {
    // Send init payload
    this._send({
      type: 'init',
      notebook: this._serialiseNotebook(),
    });

    // Send current page strokes — fire-and-forget; in journal mode this also
    // hydrates the page on first use, so opening the same page again is a
    // no-op. Folder landings short-circuit inside _ensurePageLoaded.
    const currentPage = this._pages[this._currentPageIndex];
    if (currentPage) {
      void this._navigateAndLoad(currentPage.id);
    }

    // Send vault status so the settings UI can render the right state.
    this._sendVaultStatus();

    // Send persisted settings
    persistence.loadSettings().then(settings => {
      if (settings) {
        this._send({ type: 'settings.data', payload: settings });
      }
    }).catch(() => { /* no settings yet */ });
  }

  private _handleAction(action: GrimoireAction, locallyApplied = false): void {
    // Track block/section/page state
    this._applyBlockAction(action);

    // Check if this action targets an NPC channel sigil (ephemeral, never persisted)
    const a = action as Record<string, unknown>;
    let isNpcSigilAction = false;
    if (a.blockId && typeof a.blockId === 'string' && a.pageId) {
      // After _applyBlockAction, the block state is updated.
      // Check if the resulting block is an NPC channel sigil.
      const page = this._pages.find(p => p.id === a.pageId);
      const block = page?.blocks.get(a.blockId as string);
      if (block?.kind === 'arcane') {
        const at = (block.arcaneType ?? (a.arcaneType as string | undefined)) as string | undefined;
        if (at === 'laura_channel' || at === 'ireth_channel') {
          isNpcSigilAction = true;
        }
      }
    }

    // Persist to action log (skip NPC sigil actions — they're ephemeral).
    // IDB mode: write to the local action log. Journal mode: the server picks
    // action events up from /api/_event via the bridge and writes them into
    // <root>/.ox/actions.jsonl — so the client doesn't need a second copy.
    if (!isNpcSigilAction && !this._journalMode) {
      persistence.appendAction(action).catch(console.error);
    }

    // Track strokes in memory
    if (action.type === 'ink.commit') {
      const pageId = a.pageId as string;
      if (!this._pageStrokes.has(pageId)) {
        this._pageStrokes.set(pageId, new Map());
      }
      const strokeId = a.strokeId as string;
      const rawPts = a.points as StrokeData['points'];
      this._pageStrokes.get(pageId)!.set(strokeId, {
        id: strokeId,
        points: rawPts.map(p => ({ ...p })),
        style: a.style as Record<string, unknown>,
        timestamp: action.timestamp,
        pageLayer: a.pageLayer as number | undefined,
      });
      if (this._journalMode) {
        this._schedulePageSave(pageId);
      } else {
        this._strokesDirty = true;
        this._strokesSinceLastSave++;
        if (this._strokesSinceLastSave >= 5) {
          this._strokesSinceLastSave = 0;
          this._persistStrokes();
        }
      }
    } else if (action.type === 'ink.erase') {
      const pageId = a.pageId as string;
      const target = a.target as string;
      this._pageStrokes.get(pageId)?.delete(target);
      if (this._journalMode) {
        this._schedulePageSave(pageId);
      } else {
        this._strokesDirty = true;
      }
    } else if (this._journalMode && typeof a.pageId === 'string') {
      // Any other page-targeted action (block.*, text.*, figure.*, arrow, …)
      // potentially mutated that page's contents. The _applyBlockAction pass
      // above has already updated the in-memory entry; now schedule a save.
      this._schedulePageSave(a.pageId as string);
    }

    // Handle actions that need host processing
    switch (action.type) {
      case 'page.turn': {
        if (this._journalMode) {
          this._flushJournalDirty();
        } else if (this._strokesDirty || this._indexDirty) {
          this._flushState();
        }
        const dir = a.direction === 'forward' ? 1 : -1;
        const next = this._currentPageIndex + dir;
        if (next >= 0 && next < this._pages.length) {
          this._currentPageIndex = next;
          this._currentPageId = this._pages[next]?.id;
        }
        if (this._journalMode) this._persistJournalSession();
        this._send({ type: 'action', payload: action });
        void this._navigateAndLoad(this._pages[this._currentPageIndex]?.id);
        return; // already sent
      }

      case 'page.goto': {
        if (this._journalMode) {
          this._flushJournalDirty();
        } else if (this._strokesDirty || this._indexDirty) {
          this._flushState();
        }
        const targetId = (action as Record<string, unknown>).pageId as string;
        // Folder landings: if navigating to a not-yet-loaded folder, fetch
        // it first so the landing PageEntry exists before the canvas tries
        // to render it. Real-page goto is handled by _navigateAndLoad below.
        if (this._journalMode && this.isFolderLanding(targetId)) {
          const folderPath = targetId.slice(FOLDER_LANDING_PREFIX.length);
          if (!this._loadedFolders.has(folderPath)) {
            void this._ensureFolderLoaded(folderPath).then(() => {
              const idxAfter = this._pages.findIndex(p => p.id === targetId);
              if (idxAfter !== -1) {
                this._currentPageId = targetId;
                this._currentPageIndex = idxAfter;
                this._persistJournalSession();
              }
              this._send({ type: 'action', payload: action });
              void this._navigateAndLoad(targetId);
            });
            return;
          }
        }
        this._currentPageId = targetId;
        const idx = this._pages.findIndex(p => p.id === targetId);
        if (idx !== -1) this._currentPageIndex = idx;
        if (this._journalMode) this._persistJournalSession();
        this._send({ type: 'action', payload: action });
        void this._navigateAndLoad(targetId);
        return;
      }

      case 'page.create': {
        const parentSec = this._sections.find(s => s.id === a.sectionId);
        if (parentSec) parentSec.pageIds.push(a.pageId as string);
        const sectionIdx = parentSec ? parentSec.pageIds.length - 1 : 0;
        const newPage: PageEntry = {
          id: a.pageId as string,
          index: sectionIdx,
          title: a.title as string,
          color: a.color as string | undefined,
          sectionId: a.sectionId as string | undefined,
          densityLevel: 0,
          blocks: new Map(),
          arrows: new Map(),
          updatedAt: action.timestamp,
          kind: 'page',
        };
        if (this._journalMode) {
          // Pick a filename now so the page has a stable journal path before
          // the first debounced save fires.
          //
          // Parent folder resolution:
          //   - If current page is a folder landing, the new page goes IN
          //     that folder.
          //   - Else if it's a real page, sibling of the current file.
          //   - Else (idb-only fallback), root.
          const curPage = this._pages.find(p => p.id === this._currentPageId);
          let parentFolder = '';
          if (curPage?.kind === 'folder-landing') {
            const fp = curPage.folderPath ?? '';
            parentFolder = fp ? fp.replace(/\/?$/, '/') : '';
          } else if (curPage?.journalPath) {
            parentFolder = curPage.journalPath.replace(/[^/]+$/, '');
          }
          newPage.journalPath = this._freshJournalPath(a.pageId as string, parentFolder);
        }
        this._pages.splice((a.insertAfterIndex as number) + 1, 0, newPage);
        this._currentPageIndex = (a.insertAfterIndex as number) + 1;
        this._currentPageId = a.pageId as string;
        if (this._journalMode) {
          // A user-created page is born hydrated — no remote read needed.
          this._loadedPages.add(newPage.id);
          // Splice the new file into the cached tree so sibling-aware
          // queries see it immediately.
          if (newPage.journalPath) {
            this._spliceTreeLeaf(newPage.journalPath, newPage.title);
          }
          // Create the file immediately so subsequent saves don't hit a 404.
          void this._savePageImmediate(a.pageId as string, { create: true });
          this._persistJournalSession();
        } else {
          this._indexDirty = true;
          this._flushState();
        }
        this._send({ type: 'action', payload: action });
        this._sendPageStrokes(a.pageId as string);
        return;
      }

      case 'page.delete': {
        const idx = this._pages.findIndex(p => p.id === a.pageId);
        if (idx < 0) {
          this._send({ type: 'action', payload: action });
          return;
        }
        const target = this._pages[idx];
        if (target.kind === 'folder-landing') {
          // Folder removal is a different flow; refuse here.
          this._send({ type: 'action', payload: action });
          return;
        }
        if (this._pages.length <= 1) {
          this._send({ type: 'action', payload: action });
          return;
        }
        // Unlink from section.
        if (target.sectionId) {
          const sec = this._sections.find(s => s.id === target.sectionId);
          if (sec) sec.pageIds = sec.pageIds.filter(pid => pid !== a.pageId);
        }
        // Pick a sibling to navigate to.
        const navIdx = idx + 1 < this._pages.length ? idx + 1 : idx - 1;
        const navTo = navIdx >= 0 ? this._pages[navIdx].id : undefined;
        this._pages.splice(idx, 1);
        if (this._currentPageId === a.pageId && navTo) {
          this._currentPageId = navTo;
        }
        if (this._journalMode) {
          // Best-effort: drop the file from journal storage. Errors
          // (file already gone, no removeEntry support, etc.) are
          // swallowed — the page is gone from in-memory state
          // regardless.
          if (target.journalPath && this._client?.deletePage) {
            void this._client.deletePage(target.journalPath).catch(() => {/* ignore */});
          }
          this._persistJournalSession();
        } else {
          this._indexDirty = true;
          this._flushState();
        }
        this._send({ type: 'action', payload: action });
        return;
      }

      case 'section.create': {
        if (!this._sections.some(s => s.id === a.sectionId)) {
          this._sections.push({
            id: a.sectionId as string,
            title: a.title as string,
            description: '',
            color: a.color as string,
            pageIds: [a.headerPageId as string],
            headerPageId: a.headerPageId as string,
            createdAt: action.timestamp,
          });
        }
        if (!this._pages.some(p => p.id === a.headerPageId)) {
          const hdrPage: PageEntry = {
            id: a.headerPageId as string,
            index: 0,
            title: a.title as string,
            color: a.color as string | undefined,
            sectionId: a.sectionId as string | undefined,
            isSectionHeader: true,
            densityLevel: 0,
            blocks: new Map(),
            arrows: new Map(),
            updatedAt: action.timestamp,
          };
          if (this._journalMode) {
            hdrPage.journalPath = this._freshJournalPath(a.headerPageId as string);
          }
          this._pages.splice((a.insertAfterIndex as number) + 1, 0, hdrPage);
          if (this._journalMode) {
            void this._savePageImmediate(a.headerPageId as string, { create: true });
          }
        }
        if (this._journalMode) {
          this._persistJournalSession();
        } else {
          this._indexDirty = true;
          this._flushState();
        }
        this._send({ type: 'action', payload: action });
        return;
      }

      default:
        // Echo to webview only if NOT already applied locally (avoids double-apply)
        if (!locallyApplied) {
          this._send({ type: 'action', payload: action });
        }
    }
  }

  private _handleStrokeTransform(msg: {
    pageId: string; strokeIds: string[];
    transform: { kind: string; dx?: number; dy?: number; px?: number; py?: number; sx?: number; sy?: number; angle?: number };
  }): void {
    const pageMap = this._pageStrokes.get(msg.pageId);
    if (!pageMap) return;

    for (const sid of msg.strokeIds) {
      const s = pageMap.get(sid);
      if (!s) continue;
      // Clone points to break shared references with the webview ink layer
      // (in PWA mode both sides hold the same array; mutating here would double-apply)
      s.points = s.points.map(p => ({ ...p }));
      if (msg.transform.kind === 'move') {
        for (const p of s.points) { p.x += msg.transform.dx!; p.y += msg.transform.dy!; }
      } else if (msg.transform.kind === 'scale') {
        for (const p of s.points) {
          p.x = msg.transform.px! + (p.x - msg.transform.px!) * msg.transform.sx!;
          p.y = msg.transform.py! + (p.y - msg.transform.py!) * msg.transform.sy!;
        }
      } else if (msg.transform.kind === 'rotate') {
        const cos = Math.cos(msg.transform.angle!);
        const sin = Math.sin(msg.transform.angle!);
        for (const p of s.points) {
          const rx = p.x - msg.transform.px!;
          const ry = p.y - msg.transform.py!;
          p.x = msg.transform.px! + rx * cos - ry * sin;
          p.y = msg.transform.py! + rx * sin + ry * cos;
        }
      }
    }
    if (this._journalMode) {
      this._schedulePageSave(msg.pageId);
    } else {
      this._strokesDirty = true;
    }
  }

  // ── Block state tracking (mirrors _applyBlockAction in grimoire_panel) ────

  private _applyBlockAction(action: GrimoireAction): void {
    const a = action as Record<string, unknown>;
    const pageId = a.pageId as string | undefined;
    const page = pageId ? this._pages.find(p => p.id === pageId) : undefined;

    switch (action.type) {
      case 'block.create': {
        if (!page) break;
        page.blocks.set(a.blockId as string, {
          id: a.blockId, kind: a.kind, bbox: a.bbox,
          rotation: 0, createdAt: action.timestamp, updatedAt: action.timestamp,
        });
        this._indexDirty = true;
        break;
      }
      case 'block.move': {
        const block = page?.blocks.get(a.blockId as string);
        if (block) {
          const bbox = block.bbox as { x: number; y: number; width: number; height: number };
          bbox.x += a.dx as number;
          bbox.y += a.dy as number;
          if (block.kind === 'figure') {
            const verts = block.vertices as Array<{ x: number; y: number }>;
            if (verts) for (const v of verts) { v.x += a.dx as number; v.y += a.dy as number; }
            const center = block.center as { x: number; y: number } | undefined;
            if (center) { center.x += a.dx as number; center.y += a.dy as number; }
            const edges = block.edges as Array<{ controlPoints?: Array<{ x: number; y: number }> }>;
            if (edges) for (const e of edges) {
              if (e.controlPoints) for (const cp of e.controlPoints) { cp.x += a.dx as number; cp.y += a.dy as number; }
            }
          }
          block.updatedAt = action.timestamp;
          this._indexDirty = true;
        }
        break;
      }
      case 'block.setLayer': {
        const block = page?.blocks.get(a.blockId as string);
        if (block) {
          block.pageLayer = a.pageLayer as number;
          block.updatedAt = action.timestamp;
          this._indexDirty = true;
        }
        break;
      }
      case 'block.scale': {
        const block = page?.blocks.get(a.blockId as string);
        if (block) {
          const pivot = a.pivot as { x: number; y: number };
          const sx = a.sx as number, sy = a.sy as number;
          const bbox = block.bbox as { x: number; y: number; width: number; height: number };
          block.bbox = {
            x: pivot.x + (bbox.x - pivot.x) * sx,
            y: pivot.y + (bbox.y - pivot.y) * sy,
            width: bbox.width * sx,
            height: bbox.height * sy,
          };
          block.updatedAt = action.timestamp;
          this._indexDirty = true;
        }
        break;
      }
      case 'block.delete': {
        page?.blocks.delete(a.blockId as string);
        this._indexDirty = true;
        break;
      }
      case 'block.group': {
        // newGroupId doubles as the membership tag — stamp every target.
        if (!page) break;
        const ids = a.blockIds as string[];
        const tag = a.newGroupId as string;
        for (const bid of ids) {
          const blk = page.blocks.get(bid);
          if (blk) {
            blk.groupId = tag;
            blk.updatedAt = action.timestamp;
          }
        }
        this._indexDirty = true;
        break;
      }
      case 'content.modifier.set': {
        // Patch the requested fields on each target. Blocks live in
        // `page.blocks`; strokes live in `_pageStrokes`. `null` clears,
        // `undefined` leaves the field alone.
        if (!page) break;
        const patch = a.patch as {
          locked?: boolean;
          groupId?: string | null;
          astral?: { birth: number; ttlMs: number } | null;
        };
        const targets = a.targets as Array<{ kind: 'block' | 'stroke'; id: string }>;
        const strokes = this._pageStrokes.get(pageId);
        for (const t of targets) {
          if (t.kind === 'block') {
            const blk = page.blocks.get(t.id);
            if (!blk) continue;
            if (patch.locked !== undefined) blk.locked = patch.locked;
            if (patch.groupId !== undefined) {
              if (patch.groupId === null) delete blk.groupId;
              else blk.groupId = patch.groupId;
            }
            if (patch.astral !== undefined) {
              if (patch.astral === null) delete blk.astral;
              else blk.astral = patch.astral;
            }
            blk.updatedAt = action.timestamp;
          } else if (t.kind === 'stroke' && strokes) {
            const s = strokes.get(t.id);
            if (!s) continue;
            const ss = s as { locked?: boolean; groupId?: string; astral?: { birth: number; ttlMs: number } };
            if (patch.locked !== undefined) ss.locked = patch.locked;
            if (patch.groupId !== undefined) {
              if (patch.groupId === null) delete ss.groupId;
              else ss.groupId = patch.groupId;
            }
            if (patch.astral !== undefined) {
              if (patch.astral === null) delete ss.astral;
              else ss.astral = patch.astral;
            }
          }
        }
        this._indexDirty = true;
        if (this._journalMode) this._schedulePageSave(pageId);
        break;
      }
      case 'text.update': {
        const block = page?.blocks.get(a.blockId as string);
        if (block?.kind === 'text') {
          block.content = a.content;
          block.updatedAt = action.timestamp;
          this._indexDirty = true;
        }
        break;
      }
      case 'arcane.open': {
        if (!page) break;
        const existing = page.blocks.get(a.blockId as string);
        if (existing) {
          // Update existing block (created by block.create) — preserve bbox
          existing.kind = 'arcane';
          existing.arcaneType = a.arcaneType;
          existing.state = 'open';
          existing.updatedAt = action.timestamp;
        } else {
          // Fallback: block wasn't created yet (shouldn't normally happen)
          page.blocks.set(a.blockId as string, {
            id: a.blockId, kind: 'arcane', arcaneType: a.arcaneType, state: 'open',
            bbox: { x: 100, y: 100, width: 80, height: 80 },
            rotation: 0, glyphSeed: 0, createdAt: action.timestamp, updatedAt: action.timestamp,
          });
        }
        this._indexDirty = true;
        break;
      }
      case 'arcane.close': {
        const block = page?.blocks.get(a.blockId as string);
        if (block?.kind === 'arcane') {
          block.state = 'sealed';
          this._indexDirty = true;
        }
        break;
      }
      case 'block.link': {
        if (!page) break;
        page.arrows.set(a.arrowId as string, {
          id: a.arrowId, pageId, fromId: a.fromId, toId: a.toId,
          type: a.relationType, confirmed: a.confirmed,
        });
        this._indexDirty = true;
        break;
      }
      case 'page.update': {
        const pg = this._pages.find(p => p.id === a.pageId);
        if (pg) {
          if (a.title !== undefined) pg.title = a.title as string;
          if (a.color !== undefined) pg.color = a.color as string;
          pg.updatedAt = action.timestamp;
          this._indexDirty = true;
        }
        break;
      }
      case 'page.decoration.set': {
        const pg = this._pages.find(p => p.id === a.pageId);
        if (!pg) break;
        type Decos = {
          body?: { justification?: string; graphics?: string };
          margins?: { utility?: string; graphics?: string };
          frame?: string;
        };
        const decos: Decos = (pg as { decorations?: Decos }).decorations ?? {};
        const slot = a.slot as string;
        const style = (a.style ?? null) as string | null;
        if (slot === 'frame') {
          if (style === null) delete decos.frame;
          else decos.frame = style;
        } else if (slot.startsWith('body.')) {
          const sub = slot.slice('body.'.length) as 'justification' | 'graphics';
          if (sub === 'justification' || sub === 'graphics') {
            if (!decos.body) decos.body = {};
            if (style === null) delete decos.body[sub];
            else decos.body[sub] = style;
          }
        } else if (slot.startsWith('margins.')) {
          const sub = slot.slice('margins.'.length) as 'utility' | 'graphics';
          if (sub === 'utility' || sub === 'graphics') {
            if (!decos.margins) decos.margins = {};
            if (style === null) delete decos.margins[sub];
            else decos.margins[sub] = style;
          }
        }
        // Tidy empty buckets.
        if (decos.body && !decos.body.justification && !decos.body.graphics) {
          delete decos.body;
        }
        if (decos.margins && !decos.margins.utility && !decos.margins.graphics) {
          delete decos.margins;
        }
        if (!decos.body && !decos.margins && !decos.frame) {
          delete (pg as { decorations?: unknown }).decorations;
        } else {
          (pg as { decorations?: unknown }).decorations = decos;
        }
        pg.updatedAt = action.timestamp;
        this._indexDirty = true;
        if (this._journalMode) this._schedulePageSave(pg.id);
        break;
      }
      case 'section.update': {
        const sec = this._sections.find(s => s.id === a.sectionId);
        if (sec) {
          if (a.title !== undefined) sec.title = a.title as string;
          if (a.color !== undefined) sec.color = a.color as string;
          if (a.description !== undefined) sec.description = a.description as string;
        }
        const headerPage = this._pages.find(p => p.sectionId === a.sectionId && p.isSectionHeader);
        if (headerPage) {
          if (a.title !== undefined) headerPage.title = a.title as string;
          if (a.color !== undefined) headerPage.color = a.color as string;
          headerPage.updatedAt = action.timestamp;
        }
        this._indexDirty = true;
        break;
      }
      case 'figure.create': {
        if (!page) break;
        page.blocks.set(a.blockId as string, {
          id: a.blockId, kind: 'figure', shapeKind: a.shapeKind,
          bbox: a.bbox, vertices: a.vertices, edges: a.edges,
          center: a.center, rx: a.rx, ry: a.ry,
          penSize: a.penSize, lineStyle: a.lineStyle,
          replacedStrokeIds: a.replacedStrokeIds ?? [],
          // Batch 6: kinematic envelope + ink-style snapshot for
          // ink-layer rendering of the figure body.
          renderPoints: a.renderPoints,
          inkStyle: a.inkStyle,
          rotation: 0, createdAt: action.timestamp, updatedAt: action.timestamp,
        });
        this._indexDirty = true;
        break;
      }
      case 'figure.moveVertex': {
        const block = page?.blocks.get(a.blockId as string);
        if (!block || block.kind !== 'figure') break;
        const verts = block.vertices as Array<{ x: number; y: number }>;
        const v = verts?.[a.vertexIndex as number];
        if (v) { v.x += a.dx as number; v.y += a.dy as number; }
        block.updatedAt = action.timestamp;
        this._indexDirty = true;
        break;
      }
      case 'figure.moveControlPoint': {
        const block = page?.blocks.get(a.blockId as string);
        if (!block || block.kind !== 'figure') break;
        const edges = block.edges as Array<{ controlPoints?: Array<{ x: number; y: number }> }>;
        const edge = edges?.[a.edgeIndex as number];
        const cp = edge?.controlPoints?.[a.cpIndex as number];
        if (cp) { cp.x += a.dx as number; cp.y += a.dy as number; }
        block.updatedAt = action.timestamp;
        this._indexDirty = true;
        break;
      }
      case 'figure.update': {
        const block = page?.blocks.get(a.blockId as string);
        if (!block || block.kind !== 'figure') break;
        if (a.penSize !== undefined) block.penSize = a.penSize;
        if (a.lineStyle !== undefined) block.lineStyle = a.lineStyle;
        block.updatedAt = action.timestamp;
        this._indexDirty = true;
        break;
      }
      case 'figure.split_edge': {
        const block = page?.blocks.get(a.blockId as string);
        if (!block || block.kind !== 'figure') break;
        const verts = block.vertices as Array<{ x: number; y: number }>;
        const edges = block.edges as Array<{ from: number; to: number; type: string; controlPoints?: Array<{ x: number; y: number }> }>;
        const ei = a.edgeIndex as number;
        const e = edges?.[ei];
        if (!e) break;
        // Splitting a curve / arrow needs more care than a clean
        // midpoint insert (control points, head placement). Skip
        // non-line splits for now — the dispatcher gates on type.
        if (e.type !== 'line') break;
        const newIndex = verts.length;
        verts.push({ x: a.x as number, y: a.y as number });
        const head = { from: e.from, to: newIndex, type: e.type, controlPoints: e.controlPoints };
        const tail = { from: newIndex, to: e.to, type: e.type, controlPoints: e.controlPoints };
        edges.splice(ei, 1, head, tail);
        block.updatedAt = action.timestamp;
        this._indexDirty = true;
        break;
      }
      case 'agent.write': {
        if (!page) break;
        const blkId = `agent_${Date.now().toString(36)}`;
        page.blocks.set(blkId, {
          id: blkId, kind: 'text', bbox: a.position ?? { x: 40, y: 100, width: 300, height: 60 },
          content: a.content, textStyle: a.agentId ?? 'ireth',
          rotation: 0, createdAt: action.timestamp, updatedAt: action.timestamp,
        });
        this._indexDirty = true;
        break;
      }
      case 'fold.bind': {
        // Create fold section and room pages on the host so agent.write
        // can target fold pages for persistence across reloads.
        const secId = 'sec_fold';
        if (!this._sections.some(s => s.id === secId)) {
          const roomIds = [
            ['fold_hall', 'The Fold'], ['fold_study', 'Ireth\'s Study'],
            ['fold_kitchen', 'Kitchen'], ['fold_ireth_chamber', 'Ireth\'s Chamber'],
            ['fold_laura_room', 'Laura\'s Room'], ['fold_tower', 'Tower'],
            ['fold_patio', 'Patio'], ['fold_lounge', 'Lounge'],
            ['fold_basement', 'Basement'],
          ] as const;
          this._sections.push({
            id: secId, title: 'The Fold', description: '', color: '#8B7355',
            pageIds: roomIds.map(r => r[0]),
            headerPageId: 'fold_hall',
            kind: 'fold',
          } as SectionEntry);
          for (const [pid, title] of roomIds) {
            if (!this._pages.some(p => p.id === pid)) {
              this._pages.push({
                id: pid, index: 0, title,
                sectionId: secId, isSectionHeader: pid === 'fold_hall',
                color: '#8B7355',
                densityLevel: 0, blocks: new Map(), arrows: new Map(),
                updatedAt: action.timestamp,
              });
            }
          }
          this._indexDirty = true;
        }
        break;
      }
    }
  }

  // ── Serialisation ─────────────────────────────────────────────────────────

  private _serialiseNotebook(): object {
    return {
      meta: this._meta,
      currentPageIndex: this._currentPageIndex,
      currentPageId: this._currentPageId ?? this._pages[this._currentPageIndex]?.id,
      pageCount: this._pages.length,
      sections: this._sections.map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        color: s.color,
        pageIds: s.pageIds,
        headerPageId: s.headerPageId,
        kind: s.kind,
        customizable: s.customizable,
      })),
      pages: this._pages.map(p => ({
        id: p.id,
        index: p.index,
        title: p.title,
        color: p.color,
        sectionId: p.sectionId,
        isSectionHeader: p.isSectionHeader,
        bookmark: p.bookmark,
        densityLevel: p.densityLevel,
        decorations: p.decorations,
        // Folder-landing markers — undefined for normal pages.
        kind: p.kind,
        folderPath: p.folderPath,
        description: p.description,
        blocks: [...p.blocks.values()]
          .filter(b => {
            // NPC channel sigils are ephemeral — never persist them
            if (b.kind === 'arcane') {
              const at = b.arcaneType as string;
              if (at === 'laura_channel' || at === 'ireth_channel') return false;
            }
            return true;
          })
          .map(b => this._serialiseBlock(b)),
        arrows: [...p.arrows.values()].map(a => ({
          id: a.id, fromId: a.fromId, toId: a.toId, type: a.type, confirmed: a.confirmed,
        })),
      })),
    };
  }

  private _serialiseBlock(b: Record<string, unknown>): Record<string, unknown> {
    const entry: Record<string, unknown> = {
      id: b.id, kind: b.kind, bbox: b.bbox,
    };
    if (b.rotation) entry.rotation = b.rotation;
    if (b.pageLayer != null) entry.pageLayer = b.pageLayer;
    if (b.kind === 'text') { entry.content = b.content; entry.fontSize = b.fontSize; }
    if (b.kind === 'arcane') { entry.arcaneType = b.arcaneType; entry.arcaneState = b.state; }
    if (b.kind === 'code') { entry.language = b.language; entry.fileRef = b.fileRef; }
    if (b.kind === 'figure') {
      entry.shapeKind = b.shapeKind;
      entry.vertices = b.vertices;
      entry.edges = b.edges;
      if (b.center) entry.center = b.center;
      if (b.rx !== undefined) entry.rx = b.rx;
      if (b.ry !== undefined) entry.ry = b.ry;
      if (b.penSize !== undefined) entry.penSize = b.penSize;
      if (b.lineStyle) entry.lineStyle = b.lineStyle;
      entry.replacedStrokeIds = b.replacedStrokeIds ?? [];
    }
    // Forward driver-assigned metadata (role, language, file-ref) so the
    // renderer can treat a file-body block differently from a free-floating
    // text block (e.g. apply markdown formatting for `_role: 'primary'`).
    for (const key of ['_role', '_language', '_fileRef', '_format'] as const) {
      if (b[key] !== undefined) entry[key] = b[key];
    }
    return entry;
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  private _sendPageStrokes(pageId: string): void {
    const map = this._pageStrokes.get(pageId);
    // Clone stroke points so the webview gets independent copies
    // (in PWA mode host and webview share the same JS heap)
    const strokes = map
      ? [...map.values()].map(s => ({ ...s, points: s.points.map(p => ({ ...p })) }))
      : [];
    // Also forward blocks + arrows for journal-mode pages that were just
    // hydrated. Canvas merges them into NotebookState; landings have empty
    // maps and the canvas treats them via their `kind` flag instead.
    const entry = this._pages.find(p => p.id === pageId);
    let blocks: Array<Record<string, unknown>> | undefined;
    let arrows: Array<Record<string, unknown>> | undefined;
    let landingMeta: { folderPath: string; description: string } | undefined;
    if (entry) {
      if (entry.blocks.size > 0) {
        blocks = [...entry.blocks.values()].map(b => this._serialiseBlock(b));
      }
      if (entry.arrows.size > 0) {
        arrows = [...entry.arrows.values()].map(a => ({
          id: a.id, fromId: a.fromId, toId: a.toId, type: a.type, confirmed: a.confirmed,
        }));
      }
      if (entry.kind === 'folder-landing') {
        landingMeta = {
          folderPath: entry.folderPath ?? '',
          description: entry.description ?? '',
        };
      }
    }
    this._send({ type: 'page.load', pageId, strokes, blocks, arrows, landingMeta });
  }

  /** Hydrate (if needed) then push strokes/blocks for the page. Sequencing:
   *  if contents aren't loaded yet, fetch them first so the canvas's
   *  page.load delivery includes the real blocks/arrows in one shot —
   *  otherwise the canvas paints an empty page, then a moment later flashes
   *  in the content. Folder landings short-circuit the fetch. */
  private async _navigateAndLoad(pageId: string | undefined): Promise<void> {
    if (!pageId) return;
    if (this._journalMode) {
      try {
        await this._ensurePageLoaded(pageId);
      } catch (err) {
        console.warn('[journal] page hydrate failed', pageId, err);
      }
    }
    this._sendPageStrokes(pageId);
  }

  private _send(msg: unknown): void {
    if (this._sendToWebview) {
      // Defer delivery so the call stack unwinds first (like real postMessage)
      const fn = this._sendToWebview;
      queueMicrotask(() => fn(msg));
    } else {
      // Sink not wired yet — buffer for delivery when setSendToWebview is called
      this._pendingMessages.push(msg);
    }
  }

  private _flushState(): void {
    this._persistStrokes();
    this._saveIndex().catch(console.error);
    this._indexDirty = false;
    this._strokesDirty = false;
  }

  private _persistStrokes(): void {
    if (!this._strokesDirty) return;
    persistence.saveAllStrokes(this._pageStrokes as Map<string, Map<string, persistence.StrokeData>>)
      .catch(console.error);
    this._strokesDirty = false;
  }

  private async _saveIndex(): Promise<void> {
    await persistence.saveIndex({
      schema: '1.1',
      meta: this._meta,
      pages: this._pages.map(p => ({
        id: p.id,
        index: p.index,
        title: p.title,
        color: p.color,
        sectionId: p.sectionId,
        isSectionHeader: p.isSectionHeader,
        densityLevel: p.densityLevel,
        decorations: p.decorations,
        bookmark: p.bookmark as persistence.SerialPageEntry['bookmark'],
        blockCount: p.blocks.size,
        arrowCount: p.arrows.size,
        updatedAt: p.updatedAt,
      })),
      sections: this._sections.length > 0 ? this._sections.map(s => ({
        id: s.id,
        title: s.title,
        description: s.description,
        color: s.color,
        pageIds: s.pageIds,
        headerPageId: s.headerPageId,
        kind: s.kind,
        createdAt: s.createdAt,
      })) : undefined,
      currentPageId: this._currentPageId ?? this._pages[this._currentPageIndex]?.id,
      savedAt: Date.now(),
    });
  }

  // ── Journal-mode persistence ────────────────────────────────────────────────

  /** Generate a fresh journal filename for a newly-created page. Uses the
   *  page id verbatim so the file is trivially discoverable; future work can
   *  slugify the title instead for nicer filenames in Finder.
   *
   *  `parentFolder` (forward-slash relative path, trailing `/` optional) is
   *  the folder under the journal root where the file should live. Empty
   *  string means the journal root. */
  private _freshJournalPath(pageId: string, parentFolder = ''): string {
    const safe = pageId.replace(/[^A-Za-z0-9._-]/g, '_');
    let prefix = parentFolder.replace(/\\/g, '/');
    if (prefix && !prefix.endsWith('/')) prefix += '/';
    return `${prefix}${safe}.ox.json`;
  }

  /**
   * Splice a freshly-created leaf into the cached `_journalTree` so
   * sibling-aware queries (`getJournalSiblings`) see the new page
   * immediately instead of returning null until the next full tree
   * refresh.  Without this, the side-tab rail goes blank right after
   * "+ New" because the new page's journalPath isn't in the tree yet.
   *
   * Idempotent — does nothing if the leaf already exists.
   */
  private _spliceTreeLeaf(journalPath: string, title: string): void {
    if (!this._journalTree) return;
    const slash = journalPath.lastIndexOf('/');
    const parentPath = slash >= 0 ? journalPath.slice(0, slash) : '';

    const findContainer = (node: TreeNode, path: string): TreeNode | null => {
      if (node.id === path && node.isContainer) return node;
      if (!node.isContainer) return null;
      for (const child of node.children ?? []) {
        const r = findContainer(child, path);
        if (r) return r;
      }
      return null;
    };

    const parent = findContainer(this._journalTree, parentPath);
    if (!parent) return;
    if (!parent.children) parent.children = [];
    if (parent.children.some(c => c.id === journalPath)) return;

    parent.children.push({
      id: journalPath,
      title: title || (journalPath.split('/').pop() ?? '').replace(/\.ox\.json$/i, ''),
      level: parent.level + 1,
      levelLabel: 'page',
      isContainer: false,
      driver: 'native-json',
    });
  }

  /** Mark a page dirty and (re)schedule a debounced save. Repeated calls
   *  within the debounce window coalesce into one POST. */
  private _schedulePageSave(pageId: string): void {
    if (!this._journalMode) return;
    // Folder landings have no backing file — never schedule a save for them.
    const page = this._pages.find(p => p.id === pageId);
    if (page?.kind === 'folder-landing') return;
    this._journalDirty.add(pageId);
    const existing = this._journalSaveTimers.get(pageId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this._journalSaveTimers.delete(pageId);
      void this._savePageImmediate(pageId);
    }, StandaloneHost._JOURNAL_SAVE_DEBOUNCE_MS);
    this._journalSaveTimers.set(pageId, timer);
  }

  /** Save a single page to the journal immediately. Serialises the in-memory
   *  PageEntry into the on-disk shape the Python NativeJsonDriver expects:
   *  { version, meta, blocks, strokes, arrows? }. Safe to call multiple
   *  times for the same page — overlapping calls are coalesced via
   *  _journalSavesInFlight. */
  private async _savePageImmediate(pageId: string, opts: { create?: boolean } = {}): Promise<void> {
    if (!this._journalMode) return;
    const page = this._pages.find(p => p.id === pageId);
    if (!page) {
      this._journalDirty.delete(pageId);
      return;
    }
    // Folder landings are synthetic — no on-disk file to save. Drop quietly.
    if (page.kind === 'folder-landing') {
      this._journalDirty.delete(pageId);
      return;
    }
    if (this._journalSavesInFlight.has(pageId)) {
      // A write is already on the wire — ensure dirty stays set so we'll
      // re-save after it returns.
      this._journalDirty.add(pageId);
      return;
    }
    this._journalDirty.delete(pageId);

    const journalPath = page.journalPath ?? this._freshJournalPath(pageId);
    if (!page.journalPath) page.journalPath = journalPath;

    const blocks = [...page.blocks.values()]
      .filter(b => {
        if (b.kind === 'arcane') {
          const at = b.arcaneType as string | undefined;
          if (at === 'laura_channel' || at === 'ireth_channel') return false;
        }
        return true;
      })
      .map(b => this._serialiseBlock(b));

    const arrows = [...page.arrows.values()].map(ar => ({
      id: ar.id, fromId: ar.fromId, toId: ar.toId,
      type: ar.type, confirmed: ar.confirmed,
    }));

    const strokeMap = this._pageStrokes.get(pageId);
    const strokes = strokeMap
      ? [...strokeMap.values()].map(s => ({
          id: s.id,
          points: s.points.map(p => ({ ...p })),
          style: s.style,
          timestamp: s.timestamp,
          pageLayer: s.pageLayer,
        }))
      : [];

    page.updatedAt = Date.now();
    const meta: Record<string, unknown> = {
      id: page.id,
      title: page.title,
      densityLevel: page.densityLevel,
      updatedAt: page.updatedAt,
    };
    if (page.color !== undefined) meta.color = page.color;
    if (page.sectionId !== undefined) meta.sectionId = page.sectionId;
    if (page.isSectionHeader) meta.isSectionHeader = true;
    if (page.bookmark !== undefined) meta.bookmark = page.bookmark;

    this._journalSavesInFlight.add(pageId);
    try {
      await this._client!.savePage({
        id: journalPath,
        blocks,
        strokes,
        meta,
        // The bridge doesn't yet track which pages exist on disk, so we pass
        // `create: true` whenever the caller asks OR the page is new. Safe:
        // Python only creates the file if it's missing.
        create: opts.create ?? true,
      });
    } catch (err) {
      console.error('[journal] save failed for', pageId, err);
      // Leave dirty flag set so the next auto-save pass retries.
      this._journalDirty.add(pageId);
    } finally {
      this._journalSavesInFlight.delete(pageId);
    }

    // If new dirty work arrived while the save was in flight, kick off a
    // follow-up save now that we've cleared the inflight flag.
    if (this._journalDirty.has(pageId)) {
      this._schedulePageSave(pageId);
    }
  }

  /** Fire every pending debounced save immediately. Used on navigation and
   *  shutdown to avoid losing work in the debounce window. */
  private _flushJournalDirty(): void {
    if (!this._journalMode) return;
    const pending = [...this._journalDirty];
    for (const timer of this._journalSaveTimers.values()) clearTimeout(timer);
    this._journalSaveTimers.clear();
    for (const pageId of pending) {
      void this._savePageImmediate(pageId);
    }
  }

  /** Push current-page / view state back to <root>/.ox/session.json. Called
   *  on navigation — not per-keystroke — so the file doesn't thrash. We also
   *  persist the current folder path so the next boot can pre-warm that
   *  folder alongside the root, sparing the user a navigation round-trip. */
  private _persistJournalSession(): void {
    if (!this._journalMode) return;
    const currentId = this._currentPageId ?? this._pages[this._currentPageIndex]?.id ?? null;
    const currentEntry = currentId ? this._pages.find(p => p.id === currentId) : null;
    const folderPath = currentEntry?.kind === 'folder-landing'
      ? (currentEntry.folderPath ?? '')
      : (currentEntry?.journalPath ? currentEntry.journalPath.replace(/\/?[^/]+$/, '') : '');
    void this._client!.saveSession({
      currentPageId: currentId,
      currentFolderPath: folderPath,
      savedAt: Date.now(),
    }).catch(err => console.error('[journal] session save failed', err));
  }

  // ── Action replay (for restoring state from IndexedDB) ────────────────────

  private async _replayActions(): Promise<void> {
    const actions = await persistence.loadAllActions();

    for (const action of actions) {
      const a = action as Record<string, unknown>;
      // Rebuild stroke data
      if (action.type === 'ink.commit') {
        const pageId = a.pageId as string;
        if (!this._pageStrokes.has(pageId)) {
          this._pageStrokes.set(pageId, new Map());
        }
        this._pageStrokes.get(pageId)!.set(a.strokeId as string, {
          id: a.strokeId as string,
          points: a.points as StrokeData['points'],
          style: a.style as Record<string, unknown>,
          timestamp: action.timestamp,
          pageLayer: a.pageLayer as number | undefined,
        });
      } else if (action.type === 'ink.erase') {
        this._pageStrokes.get(a.pageId as string)?.delete(a.target as string);
      }

      // Rebuild block/page/section state
      this._applyBlockAction(action);

      // Handle page/section creation (state already tracked above,
      // but pages list needs updating)
      if (action.type === 'page.create') {
        if (!this._pages.some(p => p.id === a.pageId)) {
          this._pages.splice((a.insertAfterIndex as number) + 1, 0, {
            id: a.pageId as string,
            index: 0,
            title: a.title as string,
            color: a.color as string | undefined,
            sectionId: a.sectionId as string | undefined,
            densityLevel: 0,
            blocks: new Map(),
            arrows: new Map(),
            updatedAt: action.timestamp,
          });
        }
      } else if (action.type === 'section.create') {
        if (!this._sections.some(s => s.id === a.sectionId)) {
          this._sections.push({
            id: a.sectionId as string,
            title: a.title as string,
            description: '',
            color: a.color as string,
            pageIds: [a.headerPageId as string],
            headerPageId: a.headerPageId as string,
            createdAt: action.timestamp,
          });
        }
        if (!this._pages.some(p => p.id === a.headerPageId)) {
          this._pages.splice((a.insertAfterIndex as number) + 1, 0, {
            id: a.headerPageId as string,
            index: 0,
            title: a.title as string,
            color: a.color as string | undefined,
            sectionId: a.sectionId as string | undefined,
            isSectionHeader: true,
            densityLevel: 0,
            blocks: new Map(),
            arrows: new Map(),
            updatedAt: action.timestamp,
          });
        }
      }
    }

    // Strip NPC channel sigils that may have leaked into replay
    // (block.create without matching arcane.open, or orphan arcane blocks)
    for (const page of this._pages) {
      const toRemove: string[] = [];
      for (const [blockId, block] of page.blocks) {
        if (block.kind === 'arcane') {
          const at = block.arcaneType as string | undefined;
          if (at === 'laura_channel' || at === 'ireth_channel' || !at) {
            // NPC channel or orphan arcane with no type → remove
            // (non-NPC arcane types like 'code', 'wizard' are kept)
            if (at === 'laura_channel' || at === 'ireth_channel') {
              toRemove.push(blockId);
            }
          }
        }
      }
      for (const id of toRemove) page.blocks.delete(id);
    }

    // Also load persisted strokes (may be more up-to-date than action replay
    // if some actions were lost due to crash)
    for (const page of this._pages) {
      const strokes = await persistence.loadPageStrokes(page.id);
      if (strokes.length > 0) {
        const map = new Map<string, StrokeData>();
        for (const s of strokes) map.set(s.id, s);
        // Merge: persisted strokes fill in any gaps
        const existing = this._pageStrokes.get(page.id);
        if (existing) {
          for (const [id, stroke] of map) {
            if (!existing.has(id)) existing.set(id, stroke);
          }
        } else {
          this._pageStrokes.set(page.id, map);
        }
      }
    }
  }
}
